import { useTranslation } from "react-i18next";
import { BuildingIcon, UserIcon } from "lucide-react";

import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { formatPerson } from "@/utils/helper";
import type { IUser } from "@/types/(my-space)/users.interface";

interface ProfileIdentityCardProps {
  user: IUser;
  /** Mobile-only subscription widgets — the desktop header owns these instead. */
  children?: React.ReactNode;
}

/**
 * The drawer's hero: who you are and which company you're acting in.
 *
 * The aurora wash + grid come from `.bg-profile-drawer-user`; the sheen is a
 * single on-mount sweep. The overlapping company crest repeats the header
 * trigger's visual language so the drawer reads as an expansion of the avatar
 * you clicked.
 */
export const ProfileIdentityCard = ({
  user,
  children,
}: ProfileIdentityCardProps) => {
  const { t } = useTranslation(["common"]);
  const fullName = formatPerson(user);
  const companyName = user.company?.name;

  return (
    <section className="sheen relative rounded-sm bg-profile-drawer-user p-3.5">
      <div className="flex items-center gap-3.5">
        {/* Gradient ring — a 2px brand-gradient frame around the avatar. */}
        <div className="relative shrink-0">
          {/* `rounded` (12px) outside, `rounded-sm` (10px) inside + 2px pad
              keeps the ring an even thickness around the corners. */}
          <div className="bg-gradient-brand rounded p-0.5">
            <Avatar className="size-14 rounded-sm">
              <AvatarImage src={user.profileImage ?? ""} alt={fullName} />
              <AvatarFallback className="rounded-sm">
                <UserIcon size={20} aria-hidden />
              </AvatarFallback>
            </Avatar>
          </div>
          <Avatar
            className="absolute -right-1.5 -bottom-1.5 size-6 rounded-xxs ring-2 ring-surface"
            data-tooltip-id="hrm-tooltip"
            data-tooltip-content={companyName}
          >
            <AvatarImage src={user.company?.logoIcon ?? ""} alt="" />
            <AvatarFallback className="rounded-xxs">
              <BuildingIcon size={12} aria-hidden />
            </AvatarFallback>
          </Avatar>
        </div>

        <div className="min-w-0 flex-1">
          <p
            className="truncate text-[17px] leading-tight font-semibold tracking-tight capitalize"
            title={fullName}
          >
            {fullName}
          </p>
          {companyName && (
            <p
              className="mt-0.5 truncate text-xs text-muted-foreground capitalize"
              title={companyName}
            >
              {companyName}
            </p>
          )}
          <div className="mt-2 flex flex-wrap items-center gap-1.5">
            {user.roleName && (
              // `bg-surface` + ring so the pill reads as a raised chip on the
              // hero's wash rather than dissolving into the same primary tint.
              <Badge
                variant="primary"
                className="bg-surface px-2 py-0.5 ring-1 ring-primary/15"
              >
                {user.roleName}
              </Badge>
            )}
            {user.impersonate && (
              <Badge variant="danger" className="px-2 py-0.5">
                {t("common:chrome.impersonating")}
              </Badge>
            )}
          </div>
        </div>
      </div>

      {/* `empty:*` collapses the divider when both widgets opt out (0 minutes
          saved, no token access) — otherwise a stray rule floats in the card. */}
      {children && (
        <div className="mt-3.5 grid gap-2 border-t border-primary/10 pt-3.5 empty:mt-0 empty:hidden empty:border-0 empty:pt-0 md:hidden">
          {children}
        </div>
      )}
    </section>
  );
};
